import { I18nProvider } from '@lingui/react'
import React from 'react'
import { createRoot } from 'react-dom/client'
import { i18n } from './i18n'
import './index.css'
import {
  getProperties,
  initializeFaviconAndTitle,
  initializeI18n
} from './core/utils/init-app'
import * as serviceWorker from './serviceWorker'
import { hideGlobalLoading } from './core/components/global-loading'
import { ServerOfflineScreen } from './core/components/server-offline-screen'

import { initApollo } from './core/utils/apollo'
import { initAnalytics } from './core/utils/analytics'
import { ApolloProvider } from '@apollo/client'
import { logout } from './core/utils/logout-keycloack'
import {
  AxiosMenuProvider,
  AxiosPortalProvider,
  client,
  initAxios,
  initKeycloak,
  KeycloakProvider
} from '@tecsinapse-org/portal-menu-kit'
import { QueryClientProvider } from '@tanstack/react-query'
import { SnackbarProvider } from '@tecsinapse/cortex-react'
import { PrivateApp } from './PrivateApp'

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('Failed to find the root element')
const root = createRoot(rootElement)
const isDev = import.meta.env.VITE_PROFILE === 'development'

;(async function initApp() {
  try {
    const {
      properties,
      keycloak: keycloakConfig,
      manifest
    } = await getProperties()

    const kcInstance = await initKeycloak(keycloakConfig)
    const axios = initAxios(`${import.meta.env.VITE_API_URL}`, kcInstance)
    const menuAxios = initAxios(
      `${import.meta.env.VITE_MENU_API_URL}`,
      kcInstance,
      {
        'x-menu-only': true
      }
    )
    const apolloClient = initApollo(
      `${import.meta.env.VITE_API_URL}/api/graphql`,
      kcInstance
    )

    await initializeI18n(apolloClient)
    initializeFaviconAndTitle(manifest)
    initAnalytics(!isDev ? properties.portalAnalyticsCode : undefined)

    if (kcInstance.authenticated) {
      serviceWorker.register(manifest.vapid_public_key, apolloClient)
    }
    hideGlobalLoading()

    root.render(
      <ApolloProvider client={apolloClient}>
        <KeycloakProvider
          keycloak={kcInstance}
          logout={async () => logout(apolloClient)}
        >
          <AxiosPortalProvider instance={axios}>
            <AxiosMenuProvider instance={menuAxios}>
              <QueryClientProvider client={client}>
                <I18nProvider i18n={i18n}>
                  {kcInstance.authenticated ? <PrivateApp /> : null}
                </I18nProvider>
              </QueryClientProvider>
            </AxiosMenuProvider>
          </AxiosPortalProvider>
        </KeycloakProvider>
      </ApolloProvider>
    )
  } catch (e) {
    console.error(e)
    hideGlobalLoading()
    root.render(<ServerOfflineScreen />)
  }
})()
